import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
  Typography, TextField, Grid, Box, Button, 
  Switch, FormControlLabel, FormGroup, IconButton 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';

const MemberSubscriptionPlanForm = ({
  addMemberPlan,
  updateMemberPlan,
  currentPlan,
  clearCurrentPlan,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    features: [''],
    isActive: true,
  });

  // Load current plan data when editing
  useEffect(() => {
    if (currentPlan) {
      setFormData({
        name: currentPlan.name || '',
        description: currentPlan.description || '',
        price: currentPlan.price || '',
        duration: currentPlan.duration || '',
        features: currentPlan.features || [''],
        isActive: currentPlan.isActive !== undefined ? currentPlan.isActive : true,
      });
    }
  }, [currentPlan]);

  const { name, description, price, duration, features, isActive } = formData;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onChangeFeature = (e, index) => {
    const updatedFeatures = [...features];
    updatedFeatures[index] = e.target.value;
    setFormData({ ...formData, features: updatedFeatures });
  };

  const addFeatureField = () => {
    setFormData({ ...formData, features: [...features, ''] });
  };

  const removeFeatureField = (index) => {
    const updatedFeatures = features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: updatedFeatures });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    
    // Filter out empty features
    const cleanedData = {
      ...formData,
      features: features.filter((feature) => feature.trim() !== ''),
    };
    
    if (currentPlan) {
      updateMemberPlan(currentPlan._id, cleanedData);
    } else {
      addMemberPlan(cleanedData);
    }
    
    // Reset form
    if (!currentPlan) {
      setFormData({
        name: '',
        description: '',
        price: '',
        duration: '',
        features: [''],
        isActive: true,
      });
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Typography variant="h6" gutterBottom>
        {currentPlan ? 'Edit Member Subscription Plan' : 'Add Member Subscription Plan'}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Plan Name"
            name="name"
            value={name}
            onChange={onChange}
            required
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={description}
            onChange={onChange}
            required
            multiline
            rows={3}
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Price ($)"
            name="price"
            type="number"
            value={price}
            onChange={onChange}
            required
            inputProps={{ min: "0", step: "0.01" }}
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Duration (days)"
            name="duration"
            type="number"
            value={duration}
            onChange={onChange}
            required
            inputProps={{ min: "1" }}
            variant="outlined"
          />
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Features
          </Typography>
          {features.map((feature, index) => (
            <Box key={index} sx={{ display: 'flex', mb: 2 }}>
              <TextField
                fullWidth
                label={`Feature ${index + 1}`}
                value={feature}
                onChange={(e) => onChangeFeature(e, index)}
                required
                variant="outlined"
                size="small"
              />
              <IconButton
                color="error"
                onClick={() => removeFeatureField(index)}
                disabled={features.length === 1}
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddCircleIcon />}
            onClick={addFeatureField}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Feature
          </Button>
        </Grid>
        
        <Grid item xs={12}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  name="isActive"
                  color="success"
                />
              }
              label="Active"
            />
          </FormGroup>
        </Grid>
        
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
            >
              {currentPlan ? 'Update Plan' : 'Add Plan'}
            </Button>
            {currentPlan && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={clearCurrentPlan}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

MemberSubscriptionPlanForm.propTypes = {
  addMemberPlan: PropTypes.func.isRequired,
  updateMemberPlan: PropTypes.func.isRequired,
  currentPlan: PropTypes.object,
  clearCurrentPlan: PropTypes.func.isRequired,
};

export default MemberSubscriptionPlanForm;
